import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin, HeartHandshake, Camera } from "lucide-react";
import { LineReveal, FadeIn } from "../motion";
import { HERO_IMG } from "../../data/content";
import { useLang } from "../../i18n";

const PILL_ICONS = [HeartHandshake, Camera, MapPin];

const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const cardY = useTransform(scrollYProgress, [0, 1], [0, -60]);
    const { t } = useLang();

    return (
        <section ref={ref} data-testid="hero-section" className="relative overflow-hidden">
            <div className="pointer-events-none absolute -right-40 -top-40 h-[34rem] w-[34rem] rounded-full bg-sand/50 blur-3xl" aria-hidden="true" />
            <div className="pointer-events-none absolute -left-32 bottom-0 h-96 w-96 rounded-full bg-olive-light/60 blur-3xl" aria-hidden="true" />

            <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 pb-16 pt-12 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:pb-24 lg:pt-20">
                <div className="lg:col-span-6">
                    <FadeIn delay={0.15}>
                        <p className="inline-flex items-center gap-2 rounded-full border border-[#E3DCCF] bg-white px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-olive">
                            <MapPin size={13} className="text-terracotta" />
                            {t.hero.eyebrow}
                        </p>
                    </FadeIn>

                    <h1
                        data-testid="hero-headline"
                        className="font-serif mt-6 text-4xl font-bold leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl"
                    >
                        <LineReveal delay={0.25}>{t.hero.l1}</LineReveal>
                        <LineReveal delay={0.37}>{t.hero.l2}</LineReveal>
                        <LineReveal delay={0.49}>
                            <span className="italic text-terracotta">{t.hero.l3}</span>
                        </LineReveal>
                    </h1>

                    <FadeIn delay={0.75}>
                        <p className="mt-6 max-w-lg text-base leading-relaxed text-[#525B53] sm:text-lg">
                            {t.hero.sub}
                        </p>
                    </FadeIn>

                    <FadeIn delay={0.9} className="mt-8 flex flex-wrap items-center gap-3">
                        <Link
                            to="/contacto"
                            data-testid="hero-cta-primary"
                            className="group inline-flex items-center gap-2 rounded-full bg-terracotta px-7 py-3.5 text-[15px] font-bold text-cream shadow-lg shadow-terracotta/25 transition-[background-color,transform] duration-200 hover:bg-terracotta-dark hover:-translate-y-0.5"
                        >
                            {t.hero.cta1}
                            <ArrowRight size={17} className="transition-transform duration-200 group-hover:translate-x-1" />
                        </Link>
                        <Link
                            to="/sobre-nosotros"
                            data-testid="hero-cta-secondary"
                            className="inline-flex items-center gap-2 rounded-full border border-[#E3DCCF] bg-white px-7 py-3.5 text-[15px] font-bold text-ink transition-[border-color,background-color] duration-200 hover:border-olive hover:bg-olive-light/50"
                        >
                            {t.hero.cta2}
                        </Link>
                    </FadeIn>

                    <FadeIn delay={1.05} className="mt-9 flex flex-wrap gap-2.5">
                        {t.hero.pills.map((label, i) => {
                            const Icon = PILL_ICONS[i];
                            return (
                                <span
                                    key={label}
                                    className="inline-flex items-center gap-1.5 rounded-full bg-olive-light/70 px-3.5 py-1.5 text-xs font-semibold text-olive-dark"
                                >
                                    <Icon size={13} />
                                    {label}
                                </span>
                            );
                        })}
                    </FadeIn>
                </div>

                <div className="relative lg:col-span-6">
                    <FadeIn delay={0.35} className="relative mx-auto max-w-md lg:max-w-none">
                        <div className="absolute -inset-4 -z-10 rounded-[13rem_13rem_2rem_2rem] bg-sand/70" aria-hidden="true" />
                        <motion.div style={{ y: imgY }} className="arch-frame overflow-hidden border border-[#E3DCCF] shadow-2xl shadow-ink/10">
                            <img
                                src={HERO_IMG}
                                alt={t.hero.imgAlt}
                                className="aspect-[4/5] w-full object-cover"
                                loading="eager"
                                fetchpriority="high"
                            />
                        </motion.div>
                        <motion.div
                            style={{ y: cardY }}
                            className="absolute -bottom-5 -left-4 rounded-2xl border border-[#E3DCCF] bg-white/95 px-5 py-4 shadow-xl backdrop-blur sm:-left-8"
                        >
                            <p className="font-serif text-lg font-semibold italic text-ink">{t.hero.cardTitle}</p>
                            <p className="mt-0.5 text-xs font-semibold text-[#7C877E]">{t.hero.cardSub}</p>
                        </motion.div>
                    </FadeIn>
                </div>
            </div>
        </section>
    );
};

export default Hero;
